import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';
import { editorActivityApi } from '../api/editorActivityApi';
import { isEditorRole } from '../types/auth';

// Feature 015 — the editor/data-editing surfaces. A visit to one of these is
// listed by name in the Admin view; everything else collapses to a
// "non-editing pages" count (FR-008). The SPA owns this classification
// (research §8) so the backend stays agnostic to the route table.
const EDITOR_SURFACE_PATHS = new Set<string>([
  '/data-editing',
  '/howard-hands',
  '/overcalling-data-editing',
  '/overcalling-assign-difficulty',
]);

const PAGE_LABELS: Record<string, string> = {
  '/': 'Home',
  '/dashboard': 'Dashboard',
  '/account': 'Account',
  '/session-summary': 'Session Summary',
  '/problems': 'Problems',
  '/counting-practice': 'Counting Practice',
  '/data-editing': 'Data Editing',
  '/howard-hands': 'Hands for Howard',
  '/overcalling-data-editing': 'Overcalling — Data Editing',
  '/overcalling-assign-difficulty': 'Overcalling — Assign Difficulty',
};

function labelFor(pathname: string): string {
  if (PAGE_LABELS[pathname]) return PAGE_LABELS[pathname];
  // Friendly fallback: last path segment, title-cased.
  const seg = pathname.split('/').filter(Boolean).pop() ?? 'page';
  return seg.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Feature 015 — fires a best-effort page-visit beacon on each in-app navigation,
 * but ONLY while the current user is an Editor (Admin and Public Users are never
 * tracked, FR-002). Consecutive navigations to the same path are deduped. The
 * call is fire-and-forget and swallows errors so it never blocks navigation or
 * disturbs the editor (FR-008, FR-025). Mount once in the shared Layout.
 */
export function useEditorActivityBeacon(): void {
  const location = useLocation();
  const { user, mustChangePassword } = useAuth();
  const lastReportedPath = useRef<string | null>(null);

  useEffect(() => {
    // Editor-role capture only (feature 025 adds Super Editor, whose sessions the backend
    // tracks the same way); skip while the forced-change gate is active (the
    // beacon would be blocked anyway and no navigation is possible).
    if (!user || !isEditorRole(user.role) || mustChangePassword) {
      return;
    }

    const pathname = location.pathname;
    if (lastReportedPath.current === pathname) {
      return;
    }
    lastReportedPath.current = pathname;

    editorActivityApi.recordPageVisit({
      pagePath: pathname,
      pageLabel: labelFor(pathname),
      isEditorSurface: EDITOR_SURFACE_PATHS.has(pathname),
    });
  }, [location.pathname, user, mustChangePassword]);
}
